import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPermissions } from "@/services/api";
import { useAuth } from "./useAuth";
import {
  BYPASS_ROLES,
  type PermissionKey,
  ALL_PERMISSION_KEYS,
  SpecialPermission,
  type SystemRoleValue,
} from "@/lib/rbac";

// The CRUD ops a coarse domain grant ("tasks") legitimately implies. A domain
// grant must NOT confer special, opt-in-only permissions like `tasks.view_all`
// (D1) — those are granted explicitly, never inherited from the coarse slug.
const STANDARD_OPS = new Set(["view", "create", "edit", "delete"]);

// Special permissions an admin / super_admin holds implicitly — the API's
// PermissionGuard fast-path passes them for these roles. (`tasks.view_all` is
// left out on purpose: the employee board keeps its own-tasks default.)
const BYPASS_ROLE_SPECIALS = new Set<string>([
  SpecialPermission.MediaApprove,
  SpecialPermission.LeasingApproveDiscount,
]);

/**
 * Checks if a permission key matches the effective permissions list.
 *
 * Supports all four combinations of coarse/granular keys vs coarse/granular perms:
 *  - Granular key + granular perms: exact match  ("properties.view" in ["properties.view"])
 *  - Coarse key   + granular perms: prefix match ("properties"      matches any "properties.*")
 *  - Granular key + coarse perms:   domain match ("properties.view" matches "properties")
 *  - Coarse key   + coarse perms:   exact match  ("properties"      in ["properties"])
 */
function matchesPermission(key: string, perms: readonly PermissionKey[]): boolean {
  // Exact match — works for both granular and coarse in both directions
  if (perms.includes(key as PermissionKey)) return true;
  // Coarse key against granular perms: "properties" → true if any "properties.*" exists
  if (!key.includes(".")) {
    return perms.some((p) => p.startsWith(`${key}.`));
  }
  // Granular key against coarse perms: "properties.view" → check if "properties" exists
  // (backward compat while DB transitions from coarse to granular keys). Restricted
  // to standard CRUD ops so a coarse "tasks" grant never confers `tasks.view_all`.
  const [domain, op] = key.split(".");
  if (!STANDARD_OPS.has(op)) return false;
  return perms.includes(domain as PermissionKey);
}

/**
 * Fetches user permissions via the Nest `GET /auth/permissions` endpoint
 * (Great Migration N-6 — the app-wide gate no longer touches PostgREST/Realtime).
 * Fail-closed: returns false for all checks while loading.
 * Admin/super_admin bypass: returns all permissions without a network call.
 */
export function usePermissions() {
  const { user, companyId, activeRole } = useAuth();

  const isBypassRole = BYPASS_ROLES.includes(activeRole as SystemRoleValue);

  const { data: permissions = [], isLoading } = useQuery({
    queryKey: ["user-permissions", user?.id, companyId],
    queryFn: async () => {
      if (!user?.id || !companyId) return [];
      // Nest derives company + role server-side from the verified session;
      // user.id/companyId are passed only for the local-mock env-guard fallback.
      return (await getPermissions(user.id, companyId)) as PermissionKey[];
    },
    enabled: !!user?.id && !!companyId && !isBypassRole,
    staleTime: 30_000,
    // Override the global `refetchOnWindowFocus: false` for this query
    // specifically. Permissions are the one thing that genuinely needs
    // to be revalidated when a user tabs back — an admin can change role
    // perms in a different tab, and the user shouldn't see stale gates.
    //
    // Cross-session invalidation, post-N-6: the Supabase Realtime channel
    // on `role_permissions` (which pushed sub-second updates to every
    // connected session) was removed with the PostgREST cutover. Same-session
    // propagation is preserved — the four `useSettings` mutation handlers
    // still invalidate the `["user-permissions"]` cache when an admin edits a
    // role, and a company switch changes this query key so it refetches.
    // Cross-*session* propagation now relies on this `refetchOnWindowFocus`
    // plus the 30s `staleTime` rather than an instant push. A true Nest-driven
    // signal (SSE/WebSocket gateway) is the Horizon-2 Realtime replacement
    // (docs/great-migration/specs/everything-on-nest.md) — until it lands, the
    // sub-second cross-session push is the one deliberately-accepted regression.
    refetchOnWindowFocus: true,
  });

  const effectivePermissions = useMemo(
    () => (isBypassRole ? ALL_PERMISSION_KEYS : permissions),
    [isBypassRole, permissions],
  );

  // Consider loading if:
  // 1. Query is actively loading (isLoading = true), OR
  // 2. Prerequisites not ready (user/companyId missing) AND not a bypass role
  const loading = isLoading || (!isBypassRole && (!user?.id || !companyId));

  const matches = useCallback(
    (key: string) =>
      (isBypassRole && BYPASS_ROLE_SPECIALS.has(key)) || matchesPermission(key, effectivePermissions),
    [effectivePermissions, isBypassRole],
  );

  const hasPermission = useCallback(
    (key: PermissionKey | string): boolean => {
      if (loading) return false; // fail-closed
      return matches(key as string);
    },
    [matches, loading],
  );

  const hasAnyPermission = useCallback(
    (...keys: (PermissionKey | string)[]): boolean => {
      if (loading) return false;
      return keys.some((key) => matches(key as string));
    },
    [matches, loading],
  );

  const hasAllPermissions = useCallback(
    (...keys: (PermissionKey | string)[]): boolean => {
      if (loading) return false;
      return keys.every((key) => matches(key as string));
    },
    [matches, loading],
  );

  return {
    permissions: effectivePermissions,
    isLoading: loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
}
